/**
 * The price, kept in reach wherever the cart is not.
 *
 * Below 992px antd drops the sidebar's column to `xs={24}`, which stacks the
 * whole cart underneath the form. Every announcement the cart makes — a saving
 * arriving, the total counting down — then plays somewhere the customer is not
 * looking, and on the checkout step that is the step where they type a promo
 * code. This is the same number, in the thumb zone, moving the same way.
 *
 * It is deliberately price-only. `MobileNavbar` already owns the bottom of the
 * screen on steps one to three of a phone-sized *screen*, with Back and Next on
 * it, and two fixed bars is worse than none — `App.tsx` renders this one only
 * where that one is absent.
 *
 * Never a second opinion: the figure and its name both come from `cartSummary`,
 * the same call the cart's own total row makes.
 */

import React from "react";
import { useSelector } from "react-redux";
import styled from "styled-components";
import { RootState } from "src";
import { cartSummary } from "../../lib/price";
import { usePricingConfig } from "../../hooks/usePricingConfig";
import { AnimatedCents, usePrefersReducedMotion, useRollingTotal } from "./cart-motion";
import { SAVINGS } from "./savings";
import { rollingTotalTint } from "./rolling-total";

export const StickyTotal = () => {
  const booking = useSelector((state: RootState) => state.bookingReducer);

  /** Subscribes the bar to the rates arriving from the API, as the cart does. */
  usePricingConfig();

  const { amountDueCents, totalLabel, discountCents } = cartSummary(booking);

  const reducedMotion = usePrefersReducedMotion();
  const amountRef = React.useRef<HTMLSpanElement>(null);

  /** The cart's own rule, so the bar reads as the same object moved. */
  useRollingTotal(amountRef, amountDueCents, !reducedMotion);


  return (
    <Bar
      data-testid="sticky-total"
      /*
        A summary of what is already on the page rather than an announcement of
        its own: the cart below states the same figure, and reading both out is
        the total twice.
      */
      aria-hidden="true"
    >
      <span className="label">
        {/* Without the colon: a label in a bar, not a row in a ledger. */}
        {totalLabel.replace(":", "")}
        {discountCents > 0 ? (
          <b>
            You save <AnimatedCents cents={discountCents} />
          </b>
        ) : null}
      </span>
      <span className="amount" ref={amountRef}>
        <AnimatedCents cents={amountDueCents} />
      </span>
    </Bar>
  );
};

const Bar = styled.div`
  /* Fixed, like \`MobileNavbar\`, rather than sticky: the cart it stands in for
     is the last thing on a stacked page, so a sticky bar would unpin itself
     exactly where it is still needed. \`App\` pays for it with bottom padding. */
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 3;
  /* The page reserves exactly this much room for the bar. A number the two
     agree on rather than one each: the bar grows by a line when there is a
     saving to name, and again by the safe area on a notched phone, so a
     padding guessed at either would let it cover the last control. */
  min-height: var(--sticky-total-height);
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 10px 16px calc(10px + env(safe-area-inset-bottom, 0px));
  background: #fff;
  border-top: 1px solid #bfe9f3;
  box-shadow: 0 -2px 14px rgba(20, 30, 80, 0.12);
  font-variant-numeric: tabular-nums;

  .label {
    display: flex;
    flex-direction: column;
    gap: 1px;
    min-width: 0;
    font-size: 0.8rem;
    font-weight: 500;
    color: #5c618a;
  }

  /* The one line of reinforcement the bar has room for. It is the whole reason
     a saving is worth announcing down here at all. */
  .label b {
    font-size: 0.72rem;
    font-weight: 600;
    color: ${SAVINGS.ink};
  }

  .amount {
    flex: none;
    font-size: 1.12rem;
    font-weight: 600;
    color: #323593;
    transition: color 200ms ease;
  }

  ${rollingTotalTint}
`;
